import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useRouter } from 'next/router';
import A from 'components/atoms/A/A';
import Button from 'components/atoms/Button/Button';
import Heading from 'components/atoms/Heading/Heading';
import OfferBanner from 'components/molecules/OfferBanner/OfferBanner';
import SuspenseWithIndicator from 'components/atoms/SuspenseWithIndicator/SuspenseWithIndicator';
import Tag from 'components/atoms/Tag/Tag';
import Txt from 'components/atoms/Txt/Txt';
import CashbackDeclineReasons from 'components/molecules/CashbackDeclineReasons/CashbackDeclineReasons';
import ErrorPage from 'components/pages/ErrorPage/ErrorPage';
import { useIsLoggedIn } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import tagConfigs from 'components/atoms/Tag/tagConfigs';
import { getTenantConfig } from 'utils/socialShareUtils';
import { useTenant } from 'hooks/useTenant';
import classes from './OfferLandingPage.qmee.module.scss';
import { OfferLandingPageQuery } from './__generated__/OfferLandingPageQuery.graphql';
import PageHeader from 'components/molecules/PageHeader/PageHeader.qmee';
import { extractExtraContent, type OfferType } from 'utils/offers';
import { SIGN_UP } from 'components/pages/pages';

const DynamicOfferCta = React.lazy(() => import('components/molecules/OfferCta/OfferCta'));

function constructOfferUrl(basePath: string, guid: string, homeUrl: string) {
  if (!guid) return '';
  return `${homeUrl}${basePath}/${guid}`;
}

export interface Props {
  offerType: OfferType;
}

export const QUERY = graphql`
  query OfferLandingPageQuery($input: OfferQueryInput!) {
    offer(input: $input) {
      title
      imageUrl
      desktopImageUrl
      description
      guid
      advertiser {
        name
        imageUrl
      }
      categories {
        tag
        name
      }
      rewardLabel
      terms
      tags
      locales
    }
  }
`;

const OfferLandingPage: React.FC<Props> = ({ offerType }) => {
  const router = useRouter();
  const tenant = useTenant();
  const tenantConfig = getTenantConfig(tenant);
  const offerGuid = Array.isArray(router.query.offerGuid)
    ? router.query.offerGuid[0]
    : router.query.offerGuid;
  const sectionBasePath = router.pathname.replace('/[offerGuid]', '').replace(/^\/[^/]+/, '');

  const data = useLazyLoadQuery<OfferLandingPageQuery>(
    QUERY,
    { input: { offerGuid: offerGuid || 'missing' } },
    { fetchPolicy: 'store-or-network' },
  );

  const isLoggedIn = useIsLoggedIn();
  const offer = data.offer;
  const [isOfferAvailable, setIsOfferAvailable] = useState(true);
  const handleUnavailable = useCallback(() => setIsOfferAvailable(false), []);
  const { description, extraContent } = offer?.description
    ? extractExtraContent(offerType, offer.description)
    : { description: offer?.description ?? '', extraContent: null };
  const categories = React.useMemo(
    () =>
      offer?.categories
        ? [...offer.categories]
            .filter((cat) => cat.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [offer],
  );

  if (!offer) {
    return <ErrorPage statusCode={404} />;
  }

  const isCashback = offerType === 'cashback';
  const matchingTags = (offer.tags ?? []).filter((tag) => tagConfigs[offerType][tag]);

  return (
    <div className={classes.root}>
      <PageHeader title={offer.advertiser?.name || offer.title} />

      <OfferBanner
        offerType={offerType}
        title={offer.rewardLabel || offer.title}
        bannerImageSrc={offer.advertiser?.imageUrl ?? undefined}
        description={description ?? undefined}
        tags={offer.tags ? [...offer.tags] : undefined}
        showShareOptions
        shareUrl={constructOfferUrl(sectionBasePath, offer.guid, tenantConfig.homeUrl)}
        className={classes.banner}
      />

      <div
        className={clsx(
          classes.stickyContainer,
          (!isLoggedIn || isOfferAvailable) && classes.sticky,
        )}
      >
        {isLoggedIn && offerGuid ? (
          <SuspenseWithIndicator withoutSSR>
            <DynamicOfferCta
              offerGuid={offerGuid}
              advertiserName={offer.advertiser?.name ?? undefined}
              offerTags={offer.tags}
              offerType={offerType}
              onUnavailable={handleUnavailable}
            />
          </SuspenseWithIndicator>
        ) : (
          <Button
            buttonType="primary"
            buttonSize="large"
            color="primary"
            component={A}
            href={SIGN_UP.path}
            className={classes.signUpButton}
          >
            <Txt>Sign up to enjoy our {isCashback ? 'cashback offers' : 'deals'}</Txt>
          </Button>
        )}
      </div>

      {extraContent && (
        <div className={classes.cardStyling}>
          <Heading level={3} className={classes.sectionHighlightTitle}>
            Game Rules
          </Heading>
          <Txt asMarkdown skipTranslation className={classes.sectionContent}>
            {extraContent}
          </Txt>
        </div>
      )}

      <div className={clsx(classes.cardStyling, classes.offerDetails)}>
        {matchingTags.length > 0 && (
          <div className={classes.tags}>
            {matchingTags.map((tag) => (
              <Tag key={tag} tag={tag} type={offerType} showDetails className={classes.tag} />
            ))}
          </div>
        )}

        {offerType !== 'gaming' && categories.length > 0 && (
          <div className={classes.categories}>
            {categories.map((category) => (
              <A
                href={`${sectionBasePath}?category=${category.tag}`}
                className={clsx(classes.category)}
                key={category.tag}
              >
                {category.name}
              </A>
            ))}
          </div>
        )}

        {isCashback && <CashbackDeclineReasons className={classes.declineReasons} />}

        {offer.terms && (
          <div className={classes.terms}>
            <Heading level={6} className={classes.sectionTitle}>
              Terms &amp; Conditions
            </Heading>
            <Txt skipTranslation asMarkdown className={classes.sectionContent}>
              {offer.terms}
            </Txt>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(OfferLandingPage);
